import { create } from "zustand"

import { isDemo, isProduction } from "~/utils/environment"
import {
  FEATURE_FLAG_KEYS,
  flagEnvironment,
  type FeatureFlagKey,
} from "~/features/feature-flags/shared/registry"
import { completeFlags } from "~/features/feature-flags/shared/snapshot"

type HakioFlags = Record<FeatureFlagKey, boolean>

/** Resolved values for some flags: an older server mid deploy, or older last-known flags, may lack
 * newer keys. */
type GivenFlags = Partial<HakioFlags>

/** Whose last-known flags to read and write. */
type Viewer = { orgId: string | undefined; userId: string | undefined }

type HakioFlagsState = {
  /** Undefined until the first load delivers, fails or times out; never undefined again after. */
  flags: HakioFlags | undefined
}

/** The in-house flags for the whole app, written only by `useHakioFlagsPending` at the root. */
export const useHakioFlagsStore = create<HakioFlagsState>()(() => ({ flags: undefined }))

const browserEnvironment = () => flagEnvironment({ production: isProduction(), demo: isDemo() })

const storageKey = ({ orgId, userId }: Viewer) =>
  orgId && userId ? `featureFlags.lastKnown.${orgId}.${userId}` : null

/** The stored last-known flags, keeping only registry keys with a boolean value. */
function readLastKnown(key: string | null): GivenFlags {
  const flags: GivenFlags = {}
  try {
    const stored: Record<string, unknown> =
      (key && JSON.parse(localStorage.getItem(key) ?? "null")) || {}
    for (const flagKey of FEATURE_FLAG_KEYS) {
      const value = stored[flagKey]
      if (typeof value === "boolean") flags[flagKey] = value
    }
  } catch {
    // unreadable storage or a corrupt entry: no last-known flags
  }
  return flags
}

/** The server's flags apply, and are kept as this company and user's last-known flags. */
export function receiveHakioFlags(given: GivenFlags, viewer: Viewer) {
  const flags = completeFlags(given, browserEnvironment())
  const key = storageKey(viewer)
  try {
    if (key) localStorage.setItem(key, JSON.stringify(flags))
  } catch {
    // private mode or quota: the fallback is best-effort
  }
  useHakioFlagsStore.setState({ flags })
}

/** Loading failed or timed out: the last-known flags apply, else the registry defaults. */
export function failHakioFlags(viewer: Viewer) {
  useHakioFlagsStore.setState({
    flags: completeFlags(readLastKnown(storageKey(viewer)), browserEnvironment()),
  })
}
